import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, X } from 'lucide-react';

interface TourReminderPopoverProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TourReminderPopover = ({ isOpen, onClose }: TourReminderPopoverProps) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 64, left: 0 });
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Calcular posição baseada no botão de ajuda
      const helpButton = document.querySelector('[data-tour-help-button]');
      if (helpButton) {
        const rect = helpButton.getBoundingClientRect();
        const popoverWidth = 320; // Largura fixa do popover
        const buttonCenter = rect.left + (rect.width / 2); // Centro do botão
        
        setPosition({
          top: rect.bottom + 12, // 12px abaixo do botão
          left: buttonCenter - (popoverWidth / 2), // Centralizado em relação ao centro do botão
        });

        // Fechar popover imediatamente ao clicar no botão de ajuda
        const handleHelpClick = () => {
          setShow(false);
          setTimeout(onClose, 100);
        };

        helpButton.addEventListener('click', handleHelpClick);

        // Cleanup
        const cleanup = () => {
          helpButton.removeEventListener('click', handleHelpClick);
        };

        // Pequeno delay para animação
        setTimeout(() => setShow(true), 100);

        // Auto-close após 5 segundos
        const timer = setTimeout(() => {
          setShow(false);
          setTimeout(onClose, 300); // Aguarda animação de saída
        }, 5000);

        return () => {
          clearTimeout(timer);
          cleanup();
        };
      } else {
        return () => {};
      }
    } else {
      setShow(false);
    }
  }, [isOpen, onClose]);

  const handleClose = () => {
    setShow(false);
    setTimeout(onClose, 300);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      className={`fixed z-[9999] transition-all duration-300 ${
        show ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      {/* Seta apontando para cima - centralizada */}
      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-primary rotate-45" />

      {/* Card do popover */}
      <div className="relative bg-primary text-primary-foreground rounded-lg shadow-lg p-4 w-[320px]">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-2 right-2 p-1 rounded-md text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-colors focus:outline-none"
          aria-label={t('common.close', 'Fechar')}
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <Sparkles className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-semibold text-sm">
              {t('tour.reminder.title')}
            </p>
            <p className="text-sm opacity-90">
              {t('tour.reminder.message')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
